import type { z } from 'zod';
import { useAuthStore } from '../store/authStore';
import { refreshToken as refreshAuthToken } from './auth';

function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

function toLowerCaseKeys<T>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj.map(toLowerCaseKeys) as T;
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = toCamelCase(key);
      acc[camelKey] = toLowerCaseKeys((obj as Record<string, unknown>)[key]);
      return acc;
    }, {} as Record<string, unknown>) as T;
  }
  return obj;
}

interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  retryableStatuses?: number[];
}

export class ApiError extends Error {
  status: number;
  statusText: string;

  constructor(
    message: string,
    status: number,
    statusText: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
  }
}

export class ValidationError extends Error {
  issues: z.ZodIssue[];

  constructor(message: string, issues: z.ZodIssue[]) {
    super(message);
    this.name = 'ValidationError';
    this.issues = issues;
  }
}

class ApiClient {
  private baseUrl = '';
  private defaultRetryOptions: RetryOptions = {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 10000,
    retryableStatuses: [408, 429, 500, 502, 503, 504],
  };
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;

  private getAuthHeader(): string | null {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      return `Bearer ${accessToken}`;
    }
    return null;
  }

  private async ensureValidToken(): Promise<boolean> {
    const store = useAuthStore.getState();
    if (!store.accessToken) return false;

    if (!store.isTokenExpired()) return true;

    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.doRefresh();

    try {
      return await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async doRefresh(): Promise<boolean> {
    try {
      const result = await refreshAuthToken();
      if (result) {
        const store = useAuthStore.getState();
        if (store.user) {
          store.setAuth(store.user, result.access_token, result.expires_at);
        }
        return true;
      }
      useAuthStore.getState().clearAuth();
      return false;
    } catch {
      useAuthStore.getState().clearAuth();
      return false;
    }
  }

  private async sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private calculateDelay(attempt: number, baseDelay: number, maxDelay: number): number {
    const exponentialDelay = baseDelay * Math.pow(2, attempt);
    const jitter = Math.random() * 0.3 * exponentialDelay;
    return Math.min(exponentialDelay + jitter, maxDelay);
  }

  private shouldRetry(status: number, attempt: number, maxRetries: number, retryableStatuses: number[]): boolean {
    return attempt < maxRetries && retryableStatuses.includes(status);
  }

  async request<T>(
    path: string,
    options: RequestInit = {},
    retryOptions?: RetryOptions,
    schema?: z.ZodType<T>,
    isRetryAfter401 = false
  ): Promise<T> {
    const opts = { ...this.defaultRetryOptions, ...retryOptions };
    let lastError: ApiError | null = null;

    await this.ensureValidToken();

    for (let attempt = 0; attempt <= opts.maxRetries!; attempt++) {
      try {
        const authHeader = this.getAuthHeader();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          ...options.headers as Record<string, string>,
        };
        if (authHeader) {
          headers['Authorization'] = authHeader;
        }
        const res = await fetch(`${this.baseUrl}${path}`, {
          ...options,
          credentials: 'include',
          headers,
        });

        if (!res.ok) {
          if (res.status === 401 && !isRetryAfter401) {
            const refreshed = await this.doRefresh();
            if (refreshed) {
              return this.request<T>(path, options, retryOptions, schema, true);
            }
          }

          const error = new ApiError(
            `API error: ${res.status} ${res.statusText}`,
            res.status,
            res.statusText
          );

          if (this.shouldRetry(res.status, attempt, opts.maxRetries!, opts.retryableStatuses!)) {
            lastError = error;
            const delay = this.calculateDelay(attempt, opts.baseDelay!, opts.maxDelay!);
            console.warn(`[API Client] Retry ${attempt + 1}/${opts.maxRetries} after ${Math.round(delay)}ms for ${path}`);
            await this.sleep(delay);
            continue;
          }

          throw error;
        }

        if (res.status === 204 || res.headers.get('content-length') === '0') {
          return undefined as T;
        }

        const data = await res.json();

        if (path === '/api/books' && Array.isArray(data) && data.length > 0) {
          console.log('[API Client] Raw API response (first book):', data[0]);
          const transformed = toLowerCaseKeys(data);
          console.log('[API Client] Transformed response (first book):', transformed[0]);

          if (schema) {
            const result = schema.safeParse(transformed);
            if (!result.success) {
              console.error('[API Client] Validation failed:', result.error.issues);
              throw new ValidationError('Response validation failed', result.error.issues);
            }
            return result.data;
          }

          return transformed as T;
        }

        const transformed = toLowerCaseKeys(data);

        if (schema) {
          const result = schema.safeParse(transformed);
          if (!result.success) {
            console.error('[API Client] Validation failed:', result.error.issues);
            throw new ValidationError('Response validation failed', result.error.issues);
          }
          return result.data;
        }

        return transformed as T;
      } catch (error) {
        if (error instanceof ApiError) {
          lastError = error;
          if (!this.shouldRetry(error.status, attempt, opts.maxRetries!, opts.retryableStatuses!)) {
            throw error;
          }
        } else if (error instanceof ValidationError) {
          throw error;
        } else if (error instanceof TypeError && error.message.includes('fetch')) {
          const networkError = new ApiError('Network error', 0, 'Network error');
          if (attempt < opts.maxRetries!) {
            lastError = networkError;
            const delay = this.calculateDelay(attempt, opts.baseDelay!, opts.maxDelay!);
            console.warn(`[API Client] Network retry ${attempt + 1}/${opts.maxRetries} after ${Math.round(delay)}ms`);
            await this.sleep(delay);
            continue;
          }
          throw networkError;
        } else {
          throw error;
        }
      }
    }

    throw lastError || new ApiError('Request failed after retries', 0, 'Unknown error');
  }

  get<T>(path: string, retryOptions?: RetryOptions, schema?: z.ZodType<T>): Promise<T> {
    return this.request<T>(path, {}, retryOptions, schema);
  }

  post<T>(path: string, body: unknown, retryOptions?: RetryOptions, schema?: z.ZodType<T>): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: JSON.stringify(body),
    }, retryOptions, schema);
  }

  patch<T>(path: string, body: unknown, retryOptions?: RetryOptions, schema?: z.ZodType<T>): Promise<T> {
    return this.request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }, retryOptions, schema);
  }

  put<T>(path: string, body: unknown, retryOptions?: RetryOptions, schema?: z.ZodType<T>): Promise<T> {
    return this.request<T>(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    }, retryOptions, schema);
  }

  delete<T>(path: string, retryOptions?: RetryOptions, schema?: z.ZodType<T>): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' }, retryOptions, schema);
  }

  async requestText(path: string): Promise<string> {
    await this.ensureValidToken();

    const fire = async (): Promise<Response> => {
      const auth = this.getAuthHeader();
      const headers: Record<string, string> = { Accept: 'text/markdown, text/plain' };
      if (auth) headers['Authorization'] = auth;
      return fetch(`${this.baseUrl}${path}`, { credentials: 'include', headers });
    };

    let res = await fire();
    if (res.status === 401) {
      const refreshed = await this.doRefresh();
      if (refreshed) res = await fire();
    }
    if (!res.ok) {
      throw new ApiError(`API error: ${res.status} ${res.statusText}`, res.status, res.statusText);
    }
    return res.text();
  }

  async requestBlob(path: string): Promise<{ blob: Blob; headers: Headers }> {
    await this.ensureValidToken();

    const fire = async (): Promise<Response> => {
      const auth = this.getAuthHeader();
      const headers: Record<string, string> = {};
      if (auth) headers['Authorization'] = auth;
      return fetch(`${this.baseUrl}${path}`, { credentials: 'include', headers });
    };

    let res = await fire();
    if (res.status === 401) {
      const refreshed = await this.doRefresh();
      if (refreshed) res = await fire();
    }
    if (!res.ok) {
      throw new ApiError(`API error: ${res.status} ${res.statusText}`, res.status, res.statusText);
    }
    return { blob: await res.blob(), headers: res.headers };
  }
}

export const api = new ApiClient();
